import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

// Page list with ellipses, e.g. [1, "…", 4, 5, 6, "…", 12]
const getPageItems = (currentPage, totalPages, siblings = 1) => {
  const pages = [];
  const start = Math.max(2, currentPage - siblings);
  const end = Math.min(totalPages - 1, currentPage + siblings);

  pages.push(1);
  if (start > 2) pages.push("start-ellipsis");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages - 1) pages.push("end-ellipsis");
  if (totalPages > 1) pages.push(totalPages);

  return pages;
};

const baseBtn =
  "inline-flex items-center justify-center rounded-lg border text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2470B7]/40 disabled:cursor-not-allowed disabled:opacity-50";
const inactiveBtn =
  "border-app-primary bg-app-card text-app-secondary hover:border-[#2470B7]/40 hover:bg-app-info hover:text-[#2470B7]";
const activeBtn = "border-[#2470B7] bg-[#2470B7] text-white shadow-sm";

/**
 * variant="full"    -> record summary + Previous/pages/Next (bottom of a list)
 * variant="compact" -> Previous/pages/Next only, smaller buttons (toolbars)
 */
const PaginationComponent = ({
  currentPage,
  totalPages,
  totalRecords,
  recordsPerPage,
  onPageChange,
  loading = false,
  variant = "full",
  className = "",
}) => {
  if (totalPages <= 1) return null;

  const compact = variant === "compact";
  const size = compact ? "h-8 min-w-8 px-2" : "h-9 min-w-9 px-3";
  const startRecord = (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  const goTo = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const controls = (
    <nav aria-label="Pagination" className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage === 1 || loading}
        aria-label="Previous page"
        className={`${baseBtn} ${inactiveBtn} ${size} gap-1`}
      >
        <HiChevronLeft className="h-4 w-4" />
        {!compact && <span className="hidden sm:inline">Previous</span>}
      </button>

      {getPageItems(currentPage, totalPages).map((item) =>
        typeof item === "string" ? (
          <span
            key={item}
            aria-hidden="true"
            className={`${size} inline-flex items-center justify-center text-sm text-app-muted`}
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => goTo(item)}
            disabled={loading}
            aria-label={`Page ${item}`}
            aria-current={item === currentPage ? "page" : undefined}
            className={`${baseBtn} ${size} ${
              item === currentPage ? activeBtn : inactiveBtn
            }`}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
        aria-label="Next page"
        className={`${baseBtn} ${inactiveBtn} ${size} gap-1`}
      >
        {!compact && <span className="hidden sm:inline">Next</span>}
        <HiChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );

  if (compact) return <div className={className}>{controls}</div>;

  return (
    <div
      className={`flex flex-col items-center justify-between gap-4 sm:flex-row ${className}`}
    >
      <p className="text-sm text-app-muted">
        Showing{" "}
        <span className="font-medium text-app-primary">
          {startRecord.toLocaleString()}–{endRecord.toLocaleString()}
        </span>{" "}
        of{" "}
        <span className="font-medium text-app-primary">
          {totalRecords.toLocaleString()}
        </span>{" "}
        records
      </p>
      {controls}
    </div>
  );
};

export default PaginationComponent;
